import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Leaf, MapPin, Phone, Mail, Globe, Instagram, Facebook, Sprout, Sun, Home, Warehouse, Package, Scissors, Loader2, Lock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { OwnershipBadges } from '../components/OwnershipBadges';

const API = "";

const GrowerProfile = () => {
  const { growerId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [grower, setGrower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGrower = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/api/growers/${growerId}`, { headers });
        setGrower(response.data);
      } catch (err) {
        setError('Grower not found');
      } finally {
        setLoading(false);
      }
    };
    fetchGrower();
  }, [growerId, token]);

  // Check if contact info is available (indicates user is approved dispensary)
  const hasContactInfo = grower?.email || grower?.phone;

  const getGrowTypeIcon = (type) => {
    switch (type) {
      case 'indoor': return Home;
      case 'outdoor': return Sun;
      case 'greenhouse': return Warehouse;
      default: return Sprout;
    }
  };

  const getGrowTypeLabel = (type) => {
    switch (type) {
      case 'indoor': return 'Indoor Grow';
      case 'outdoor': return 'Outdoor Cultivation';
      case 'greenhouse': return 'Greenhouse';
      case 'mixed': return 'Mixed Methods';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  if (error || !grower) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Leaf className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Grower Not Found</h2>
            <p className="text-muted-foreground mb-4">This grower profile doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/canna-club')}>
              Back to Canna-Club
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const GrowTypeIcon = getGrowTypeIcon(grower.grow_type);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-green-950/10 to-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-lg font-bold truncate">{grower.business_name}</h1>
        </div>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative"
      >
        {grower.image_url ? (
          <div className="aspect-video sm:aspect-[21/9] w-full overflow-hidden">
            <img
              src={grower.image_url}
              alt={grower.business_name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.classList.add('bg-gradient-to-br', 'from-green-900/30', 'to-green-600/10', 'flex', 'items-center', 'justify-center');
                const leaf = document.createElement('div');
                leaf.innerHTML = '<svg class="w-24 h-24 text-green-500/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></svg>';
                e.target.parentElement.appendChild(leaf.firstChild);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        ) : (
          <div className="aspect-video sm:aspect-[21/9] w-full bg-gradient-to-br from-green-900/30 to-green-600/10 flex items-center justify-center relative">
            <Leaf className="w-24 h-24 text-green-500/30" />
          </div>
        )}

        {/* Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <Leaf size={12} className="mr-1" /> Grower
              </Badge>
              <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                <GrowTypeIcon size={12} className="mr-1" /> {getGrowTypeLabel(grower.grow_type)}
              </Badge>
              {grower.is_verified && (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  <CheckCircle size={12} className="mr-1" /> Verified
                </Badge>
              )}
              {grower.is_featured && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  Featured
                </Badge>
              )}
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-1">{grower.business_name}</h1>
            <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2">
              <MapPin size={14} /> {grower.city}, Minnesota
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 space-y-4">
        
        {/* Ownership Badges */}
        {(grower.women_owned || grower.native_owned || grower.bipoc_owned) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            <OwnershipBadges 
              womenOwned={grower.women_owned} 
              nativeOwned={grower.native_owned} 
              bipocOwned={grower.bipoc_owned}
              size="default"
            />
          </motion.div>
        )}

        {/* Services */}
        {(grower.accepts_wholesale || grower.offers_clones || grower.offers_seeds) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-2"
          >
            {grower.accepts_wholesale && (
              <Badge variant="outline" className="px-3 py-1">
                <Package size={14} className="mr-1" /> Wholesale Available
              </Badge>
            )}
            {grower.offers_clones && (
              <Badge variant="outline" className="px-3 py-1">
                <Scissors size={14} className="mr-1" /> Clones Available
              </Badge>
            )}
            {grower.offers_seeds && (
              <Badge variant="outline" className="px-3 py-1">
                <Sprout size={14} className="mr-1" /> Seeds Available
              </Badge>
            )}
          </motion.div>
        )}

        {/* About */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card/50 backdrop-blur border-border">
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Leaf size={18} className="text-green-500" /> About
              </h2>
              <p className="text-muted-foreground leading-relaxed">{grower.description}</p>
              
              {grower.specialties && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium mb-2">Specialties</h3>
                  <p className="text-sm text-green-400">{grower.specialties}</p>
                </div>
              )}

              {grower.years_experience && (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>{grower.years_experience}+</strong> years of growing experience
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact - Only shown if user is logged in */}
        {user ? (
          hasContactInfo && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-card/50 backdrop-blur border-border">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg font-semibold mb-4">Contact</h2>
                  <div className="space-y-3">
                    {grower.email && (
                      <a href={`mailto:${grower.email}`} className="flex items-center gap-3 text-sm hover:text-green-400 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                          <Mail size={18} className="text-green-500" />
                        </div>
                        <span className="break-all">{grower.email}</span>
                      </a>
                    )}
                    {grower.phone && (
                      <a href={`tel:${grower.phone}`} className="flex items-center gap-3 text-sm hover:text-green-400 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                          <Phone size={18} className="text-green-500" />
                        </div>
                        <span>{grower.phone}</span>
                      </a>
                    )}
                    {grower.website && (
                      <a href={grower.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-green-400 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                          <Globe size={18} className="text-green-500" />
                        </div>
                        <span>Website</span>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )
        ) : (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-card/50 backdrop-blur border-green-500/20">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
                  <Lock size={24} className="text-green-500" />
                </div>
                <h3 className="font-semibold mb-2">Sign In to View Contact Info</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Email, phone, and social media links are only visible to registered users.
                </p>
                <Button asChild variant="outline" className="border-green-500/50">
                  <a href="/login">Sign In</a>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Social Links - Only shown if user is logged in */}
        {user && (grower.instagram || grower.facebook) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardContent className="p-4 sm:p-6">
                <h2 className="text-lg font-semibold mb-4">Social Media</h2>
                <div className="grid grid-cols-2 gap-3">
                  {grower.instagram && (
                    <a
                      href={grower.instagram.startsWith('http') ? grower.instagram : `https://instagram.com/${grower.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                    >
                      <Instagram size={20} className="text-pink-400" />
                      <span className="text-sm font-medium">Instagram</span>
                    </a>
                  )}
                  {grower.facebook && (
                    <a
                      href={grower.facebook.startsWith('http') ? grower.facebook : `https://facebook.com/${grower.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 transition-all"
                    >
                      <Facebook size={20} className="text-blue-400" />
                      <span className="text-sm font-medium">Facebook</span>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* CTA */}
        {hasContactInfo && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
              <CardContent className="p-4 sm:p-6 text-center">
                <h3 className="font-semibold mb-2">Interested in their products?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Reach out directly to discuss wholesale orders, clones, or seeds
                </p>
                <a href={`mailto:${grower.email}?subject=Inquiry from Twin Tube Canna-Club`}>
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Mail size={16} className="mr-2" />
                    Contact Grower
                  </Button>
                </a>
              </CardContent>
            </Card>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default GrowerProfile;
